import React from 'react'
import { RouterProvider, createBrowserRouter } from 'react-router-dom'
import App from './App'
import { PrimaryLayout, SecondaryLayout } from './layouts'
import {
  ArkUserEdit,
  ArkUserList,
  CampaignEdit,
  ConsumerEdit,
  ConsumerList,
  Content,
  ContentEdit,
  ContentNew,
  ForgotPassword,
  HomePage,
  LoginPage,
  Onboarding,
  RegisterPage,
  ResetPassword,
  TemplateEdit,
  TemplateList,
  TenantEdit,
  TenantList,
  Unauthorized,
  SkillBiteList,
  SkillBiteNew,
  SkillBiteEdit,
  AssessmentList,
  AssessmentEdit,
  Acquisition,
  SkillsPracticeList,
  SkillsPracticeEdit
} from './pages'
import {
  ALLOW_ARK_USERS_TAB,
  ALLOW_CLIENT_TOOLKITS_TAB,
  ALLOW_CONSUMER_USERS_TAB,
  ALLOW_CONTENT_TAB,
  ALLOW_CAMPAIGN_TAB,
  ALLOW_TEMPLATES_TAB,
  ALLOW_SKILL_BITES_TAB,
  ALLOW_ASSESSMENTS_TAB,
  ALLOW_ACQUISITION_TAB,
  ALLOW_SKILLS_PRACTICE_TAB
} from './util/permissions'
import { CLIENT_MANAGER } from './util/userGroups'
import { useTenant } from '@emerald-works-nova/auth'
import { useArkMode } from './hooks/useArkMode'
import { ArkPrivateRoute } from './components/ark-private-route'
import { SignInCompletion } from '@emerald-works/react-auth'

const getGroups = (allowedGroups) => new RegExp(allowedGroups.join('|'))

export const routes = {
  home: { path: `${process.env.PUBLIC_URL}/` },
  login: { path: '/login' },
  forgotPassword: { path: `${process.env.PUBLIC_URL}/forgot-password` },
  resetPassword: { path: `${process.env.PUBLIC_URL}/reset/:code/:username` },
  arkUsers: { path: `${process.env.PUBLIC_URL}/ark-users/` },
  arkUserEdit: { path: `${process.env.PUBLIC_URL}/ark-users/:id` },
  acquisition: { path: `${process.env.PUBLIC_URL}/acquisition` },
  campaignNew: { path: `${process.env.PUBLIC_URL}/campaigns/new` },
  campaignDetails: { path: `${process.env.PUBLIC_URL}/campaigns/:id` },
  consumerList: { path: `${process.env.PUBLIC_URL}/consumers/` },
  consumerEdit: { path: `${process.env.PUBLIC_URL}/consumers/:id` },
  contentList: { path: `${process.env.PUBLIC_URL}/contents` },
  contentNew: { path: `${process.env.PUBLIC_URL}/contents/new` },
  contentEdit: { path: `${process.env.PUBLIC_URL}/contents/:locationId*` },
  onboarding: { path: `${process.env.PUBLIC_URL}/onboarding` },
  templateList: { path: `${process.env.PUBLIC_URL}/templates` },
  templateEdit: { path: `${process.env.PUBLIC_URL}/templates/:id` },
  tenantList: { path: `${process.env.PUBLIC_URL}/tenants` },
  tenantNew: { path: `${process.env.PUBLIC_URL}/tenants/new` },
  tenantDetails: { path: `${process.env.PUBLIC_URL}/tenants/:id` },
  register: { path: `${process.env.PUBLIC_URL}/register` },
  skillBiteList: { path: `${process.env.PUBLIC_URL}/skill-bites` },
  skillBiteNew: { path: `${process.env.PUBLIC_URL}/skill-bites/new` },
  skillBiteEdit: { path: `${process.env.PUBLIC_URL}/skill-bites/:skillBiteId` },
  assessmentsList: { path: `${process.env.PUBLIC_URL}/assessments` },
  assessmentEdit: { path: `${process.env.PUBLIC_URL}/assessments/:assessmentId` },
  skillsPracticeList: { path: `${process.env.PUBLIC_URL}/skills-practice` },
  skillsPracticeNew: { path: `${process.env.PUBLIC_URL}/skills-practice/new` },
  skillsPracticeEdit: { path: `${process.env.PUBLIC_URL}/skills-practice/:moduleId` },
  unauthorized: { path: `${process.env.PUBLIC_URL}/unauthorized` },
  signin: { path: '/signin' }
}

const appRoutes = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      {
        element: <PrimaryLayout />,
        children: [
          {
            path: routes.home.path,
            element: <ArkPrivateRoute element={<HomePage />} />
          },
          {
            path: routes.arkUsers.path,
            element: <ArkPrivateRoute element={<ArkUserList />} allowedUserGroups={getGroups(ALLOW_ARK_USERS_TAB)} />
          },
          {
            path: routes.arkUserEdit.path,
            element: <ArkPrivateRoute element={<ArkUserEdit />} allowedUserGroups={getGroups(ALLOW_ARK_USERS_TAB)} />
          },
          {
            path: routes.acquisition.path,
            element: <ArkPrivateRoute element={<Acquisition />} allowedUserGroups={getGroups(ALLOW_ACQUISITION_TAB)} />
          },
          {
            path: routes.campaignNew.path,
            element: <ArkPrivateRoute element={<CampaignEdit />} allowedUserGroups={getGroups(ALLOW_CAMPAIGN_TAB)} />
          },
          {
            path: routes.campaignDetails.path,
            element: <ArkPrivateRoute element={<CampaignEdit />} allowedUserGroups={getGroups(ALLOW_CAMPAIGN_TAB)} />
          },
          {
            path: routes.consumerList.path,
            element: <ArkPrivateRoute element={<ConsumerList />} allowedUserGroups={getGroups(ALLOW_CONSUMER_USERS_TAB)} />
          },
          {
            path: routes.consumerEdit.path,
            element: <ArkPrivateRoute element={<ConsumerEdit />} allowedUserGroups={getGroups(ALLOW_CONSUMER_USERS_TAB)} />
          },
          {
            path: routes.contentList.path,
            element: <ArkPrivateRoute element={<Content />} allowedUserGroups={getGroups(ALLOW_CONTENT_TAB)} />
          },
          {
            path: routes.contentNew.path,
            element: <ArkPrivateRoute element={<ContentNew />} allowedUserGroups={getGroups(ALLOW_CONTENT_TAB)} />
          },
          {
            path: routes.contentEdit.path,
            element: <ArkPrivateRoute element={<ContentEdit />} allowedUserGroups={getGroups(ALLOW_CONTENT_TAB)} />
          },
          {
            path: routes.templateList.path,
            element: <ArkPrivateRoute element={<TemplateList />} allowedUserGroups={getGroups(ALLOW_TEMPLATES_TAB)} />
          },
          {
            path: routes.templateEdit.path,
            element: <ArkPrivateRoute element={<TemplateEdit />} allowedUserGroups={getGroups(ALLOW_TEMPLATES_TAB)} />
          },
          {
            path: routes.tenantList.path,
            element: <ArkPrivateRoute element={<TenantList />} allowedUserGroups={getGroups(ALLOW_CLIENT_TOOLKITS_TAB)} />
          },
          {
            path: routes.tenantNew.path,
            element: <ArkPrivateRoute element={<TenantEdit />} allowedUserGroups={getGroups(ALLOW_CLIENT_TOOLKITS_TAB)} />
          },
          {
            path: routes.tenantDetails.path,
            element: <ArkPrivateRoute element={<TenantEdit />} allowedUserGroups={getGroups(ALLOW_CLIENT_TOOLKITS_TAB)} />
          },
          {
            path: routes.skillBiteList.path,
            element: <ArkPrivateRoute element={<SkillBiteList />} allowedUserGroups={getGroups(ALLOW_SKILL_BITES_TAB)} />
          },
          {
            path: routes.skillBiteNew.path,
            element: <ArkPrivateRoute element={<SkillBiteNew />} allowedUserGroups={getGroups(ALLOW_SKILL_BITES_TAB)} />
          },
          {
            path: routes.skillBiteEdit.path,
            element: <ArkPrivateRoute element={<SkillBiteEdit />} allowedUserGroups={getGroups(ALLOW_SKILL_BITES_TAB)} />
          },
          {
            path: routes.assessmentsList.path,
            element: <ArkPrivateRoute element={<AssessmentList />} allowedUserGroups={getGroups(ALLOW_ASSESSMENTS_TAB)} />
          },
          {
            path: routes.assessmentEdit.path,
            element: <ArkPrivateRoute element={<AssessmentEdit />} allowedUserGroups={getGroups(ALLOW_ASSESSMENTS_TAB)} />
          },
          {
            path: routes.skillsPracticeList.path,
            element: <ArkPrivateRoute element={<SkillsPracticeList />} allowedUserGroups={getGroups(ALLOW_SKILLS_PRACTICE_TAB)} />
          },
          {
            path: routes.skillsPracticeNew.path,
            element: <ArkPrivateRoute element={<SkillsPracticeEdit />} allowedUserGroups={getGroups(ALLOW_SKILLS_PRACTICE_TAB)} />
          },
          {
            path: routes.skillsPracticeEdit.path,
            element: <ArkPrivateRoute element={<SkillsPracticeEdit />} allowedUserGroups={getGroups(ALLOW_SKILLS_PRACTICE_TAB)} />
          }
        ]
      },
      {
        element: <SecondaryLayout />,
        children: [
          {
            path: routes.login.path,
            element: <LoginPage />
          },
          {
            path: routes.forgotPassword.path,
            element: <ForgotPassword />
          },
          {
            path: routes.resetPassword.path,
            element: <ResetPassword />
          },
          {
            path: routes.unauthorized.path,
            element: <Unauthorized />
          },
          {
            path: routes.register.path,
            element: <RegisterPage />
          }
        ]
      },
      {
        path: routes.onboarding.path,
        element: <ArkPrivateRoute element={<Onboarding />} allowedUserGroups={/.*/} />
      },
      {
        path: routes.signin.path,
        element: <SignInCompletion />
      }
    ]
  }
])

const sbtRoutes = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      {
        element: <PrimaryLayout />,
        children: [
          {
            path: routes.home.path,
            element: <ArkPrivateRoute element={<HomePage />} />
          },
          {
            path: routes.tenantDetails.path,
            element: <ArkPrivateRoute element={<TenantEdit />} allowedUserGroups={getGroups([CLIENT_MANAGER])} />
          }
        ]
      },
      {
        element: <SecondaryLayout />,
        children: [
          {
            path: routes.login.path,
            element: <LoginPage />
          },
          {
            path: routes.unauthorized.path,
            element: <Unauthorized />
          }
        ]
      },
      {
        path: routes.signin.path,
        element: <SignInCompletion />
      }
    ]
  }
])

export const ApplicationRouter = () => {
  const tenant = useTenant()
  const { isAdminMode } = useArkMode()

  if (!tenant) return null

  return (
    <RouterProvider
      router={isAdminMode ? appRoutes : sbtRoutes}
    />
  )
}
